<template>
    <app-layout title="Survey Template Management">
        <template v-slot:header>
            <div class="flex justify-between">
                <h2 class="font-bold text-4xl dark:text-gray-200">
                    Survey Templates
                </h2>
                <inertia-link v-if="canManageTemplates" :href="route('admin.survey.templates.create')" class="max-w-min whitespace-nowrap px-4 py-2 flex items-center text-lg font-normal leading-5 rounded-md text-white border border-transparent focus:outline-none focus:shadow-none disabled:opacity-25 transition transition-all duration-150 bg-red-0 hover:bg-red-0h">
                    Create survey template
                    <span class="ml-2 text-white" aria-hidden="true">+</span>
                </inertia-link>
            </div>
        </template>
        <div class="p-6 shadow-md rounded-md">
            <template v-if="paginatedTemplates.data.length">
                <survey-card v-for="template in paginatedTemplates.data">
                    <template v-slot:data>
                        <div>
                            <p class="text-2xl leading-6 font-bold mb-2">{{ template.name }}</p>
                            <p>Created: {{ moment(template.created_at).format('DD-MM-YYYY HH:mm') }}</p>
                        </div>
                        <div class="xl:text-right">
                            <p class="text-2xl">{{ template.survey_type.type }}</p>
                        </div>
                    </template>
                    <template v-slot:actions>
                        <pill :type="pillColorsByStatus[template.status]">
                            {{ template.current_status }}
                        </pill>
                        <inertia-link class="flex items-center font-bold text-lg leading-6"
                                      :href="route('admin.survey.templates.show', template.id)"
                        >
                            <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                            </svg>
                            Preview template
                        </inertia-link>
                        <template v-if="canManageTemplates">
                            <button class="flex items-center font-bold text-lg leading-6"
                                    v-if="template.status === 1"
                                    @click.prevent="togglePublish(template)"
                            >
                                <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512">
                                    <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/>
                                </svg>
                                Unpublish template
                            </button>
                            <button class="flex items-center font-bold text-lg leading-6"
                                    v-else-if="template.status === 2"
                                    @click.prevent="togglePublish(template)"
                            >
                                <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                    <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"/>
                                </svg>
                                Publish template
                            </button>
                            <inertia-link class="flex items-center font-bold text-lg leading-6"
                                          :href="route('admin.survey.templates.edit', template.id)"
                            >
                                <svg class="mr-2 w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                </svg>
                                Edit template
                            </inertia-link>
                            <button class="flex items-center font-bold text-lg leading-6"
                                    @click.prevent="deleteTemplate(template)"
                            >
                                <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                    <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"/>
                                </svg>
                                Delete template
                            </button>
                        </template>
                    </template>
                </survey-card>
                <table-pagination :pagination-object="paginatedTemplates"/>
            </template>

            <template v-else>
                <p class="text-2xl font-bold leading-6 text-center">No templates</p>
            </template>
        </div>

        <!--<div class="w-full overflow-hidden rounded-lg shadow mt-10">
            <table-layout>
                <template v-slot:theadRow>
                    <th class="px-4 py-3">Name</th>
                    <th class="px-4 py-3">Type</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Created at</th>
                    <th class="px-4 py-3">Actions</th>
                </template>
                <template v-slot:tbody v-if="paginatedTemplates.data.length">
                    <tr v-for="template in paginatedTemplates.data" class="text-gray-700 dark:text-gray-400">
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div>
                                    <p class="font-semibold">{{ template.name }}</p>
                                    <p class="text-xs text-gray-600 dark:text-gray-400">
                                        {{ template.user ? template.user.name : '' }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div>
                                    {{ template.survey_type.type }}
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-xs ">
                            <div class="flex items-center">
                                <pill :type="pillColorsByStatus[template.status]">
                                    {{ template.current_status }}
                                </pill>
                                <template v-if="canManageTemplates">
                                    <span v-if="template.status === 1"
                                          @click.prevent="togglePublish(template)"
                                          class="cursor-pointer ml-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                </span>
                                    <span v-else-if="template.status === 2"
                                          @click.prevent="togglePublish(template)"
                                          class="cursor-pointer ml-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </span>
                                </template>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ moment(template.created_at).format("DD-MM-YYYY HH:mm") }}
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center space-x-1 text-sm">
                                <inertia-link :href="route('admin.survey.templates.show', template.id)" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="Show"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </inertia-link>
                                <template v-if="canManageTemplates">
                                    <inertia-link :href="route('admin.survey.templates.edit', template.id)" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                                  aria-label="Edit"
                                    >
                                        <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                        </svg>
                                    </inertia-link>
                                    <button @click.prevent="deleteTemplate(template)" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                            aria-label="Delete"
                                    >
                                        <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                        </svg>
                                    </button>
                                </template>
                            </div>
                        </td>
                    </tr>
                </template>
                <template v-slot:tbody v-else>
                    <tr class="text-center text-gray-700 dark:text-gray-400">
                        <td colspan="5">
                            No templates
                        </td>
                    </tr>
                </template>
            </table-layout>
            <div class="p-3 bg-gray-50">
                <table-pagination :pagination-object="paginatedTemplates"/>
            </div>
        </div>-->
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import SurveyCard from "@/Components/SurveyCard";
import Pill from "@/Jetstream/Pill";
import { Inertia } from '@inertiajs/inertia';
import { inject } from 'vue'
import {usePage} from "@inertiajs/inertia-vue3";

export default defineComponent({
    components: {
        Pill,
        TableLayout,
        SurveyCard,
        TablePagination,
        AppLayout
    },
    props: {
        paginatedTemplates: Object,
    },
    setup(props) {
        const canManageTemplates = usePage().props.value.allUserPermissions
            && usePage().props.value.allUserPermissions.indexOf('manage survey templates') !== -1;

        const pillColorsByStatus = [
            'warning',
            'success',
            'danger'
        ];
        const $swal = inject('$swal');

        const deleteTemplate = (template) => {
            $swal({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.delete(route('admin.survey.templates.destroy', template.id));
                }
            });
        }

        const togglePublish = (template) => {
            $swal({
                title: 'Are you sure you want to ' + (template.status === 1 ? 'unpublish' : 'publish') + ' this survey template?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes ' + (template.status === 1 ? 'unpublish' : 'publish') + ' it!',
                cancelButtonText: 'No',
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.put(route('admin.survey.templates.togglePublish', template.id));
                }
            });
        }

        return {
            deleteTemplate,
            pillColorsByStatus,
            togglePublish,
            canManageTemplates,
        };
    }
})
</script>
